import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Users,
  PartyPopper,
  CalendarDays,
  Sparkles,
  MapPin,
  Mail,
  ExternalLink,
  X,
  Trash2,
  RotateCcw,
  CheckCircle,
  Instagram,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  ScanLine,
} from "lucide-react";
import {
  useAdminKikis,
  useAdminKikiDetail,
  useSetKikiStatus,
  useReviewKikiFlag,
  useScanKikis,
  type AdminKiki,
  type AdminKikiApplication,
  type KikiStatus,
  type KikiKind,
  type AdminKikisFilters,
} from "../api/useAdminKikis";
import { useToast } from "@/components/ux/Toast";
import { Loader } from "@/components/Loader";

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

function Avatar({ url, name, size = 36 }: { url: string | null; name: string; size?: number }) {
  const initial = name?.trim()?.[0]?.toUpperCase() ?? "?";
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        className="rounded-full object-cover bg-dark-card shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-primary-500/20 text-primary-300 flex items-center justify-center font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.42 }}
    >
      {initial}
    </div>
  );
}

function KikiStatusBadge({ status }: { status: KikiStatus }) {
  if (status === "active")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-400">
        <CheckCircle className="w-3 h-3" /> Active
      </span>
    );
  if (status === "fulfilled")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sky-500/15 text-sky-400">
        <Sparkles className="w-3 h-3" /> Fulfilled
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/15 text-red-400">
      <Trash2 className="w-3 h-3" /> Taken down
    </span>
  );
}

function KindBadge({ kind }: { kind: KikiKind }) {
  return kind === "event" ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-500/15 text-violet-300">
      <CalendarDays className="w-3 h-3" /> Event
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-300">
      <PartyPopper className="w-3 h-3" /> Standalone
    </span>
  );
}

function AppStatusBadge({ status }: { status: string }) {
  if (status === "accepted")
    return <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">accepted</span>;
  if (status === "declined")
    return <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-500/15 text-neutral-400">declined</span>;
  return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300">pending</span>;
}

const CATEGORY_LABELS: Record<string, string> = {
  sexual: "Sexual",
  solicitation: "Solicitation",
  harassment: "Harassment",
  hate: "Hate",
  violence: "Violence",
  illegal: "Illegal",
  self_harm: "Self-harm",
  spam: "Spam",
  other: "Other",
};

// Shows on flagged kikis. "needsReview" (flagged + unacknowledged) is loud red;
// an already-reviewed flag is muted.
function FlagBadge({ needsReview }: { needsReview: boolean }) {
  return needsReview ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-500/20 text-red-300 ring-1 ring-red-500/40">
      <ShieldAlert className="w-3 h-3" /> Flagged
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-500/15 text-neutral-400">
      <ShieldCheck className="w-3 h-3" /> Flag reviewed
    </span>
  );
}

function kikiTitle(k: Pick<AdminKiki, "kind" | "eventName" | "activity" | "activityInfo">): string {
  if (k.kind === "event") return k.eventName ?? "Event kiki";
  return k.activityInfo?.name || k.activity || "Standalone kiki";
}

function StatCard({
  label,
  value,
  icon: Icon,
  tone = "default",
}: {
  label: string;
  value: number | string;
  icon: typeof Users;
  tone?: "default" | "good" | "warn" | "bad";
}) {
  const toneClass =
    tone === "good"
      ? "text-emerald-400"
      : tone === "warn"
        ? "text-amber-300"
        : tone === "bad"
          ? "text-red-400"
          : "text-primary-400";
  return (
    <div className="card flex items-center gap-3 py-3">
      <div className={`shrink-0 ${toneClass}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xl font-bold leading-none">{value}</div>
        <div className="text-xs text-dark-muted mt-1 truncate">{label}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kiki list row
// ---------------------------------------------------------------------------

function KikiRow({ kiki, onOpen }: { kiki: AdminKiki; onOpen: (id: string) => void }) {
  return (
    <motion.button
      type="button"
      onClick={() => onOpen(kiki.id)}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className={`card w-full text-left transition-colors ${
        kiki.needsReview
          ? "ring-1 ring-red-500/40 hover:ring-red-500/60"
          : "hover:border-primary-500/40"
      }`}
    >
      <div className="flex items-start gap-3">
        <Avatar url={kiki.posterProfileImageUrl} name={kiki.posterName} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm truncate">{kikiTitle(kiki)}</span>
            <KindBadge kind={kiki.kind} />
            <KikiStatusBadge status={kiki.status} />
            {kiki.flaggedAt && <FlagBadge needsReview={kiki.needsReview} />}
          </div>
          <p className="text-xs text-dark-muted mt-0.5">
            by {kiki.posterName}
            {kiki.cityName ? ` · ${kiki.cityName}` : ""}
            {" · "}
            {new Date(kiki.createdAt).toLocaleDateString()}
          </p>
          {kiki.needsReview && kiki.flagReason && (
            <p className="text-xs mt-1.5 text-red-300 flex items-start gap-1">
              <ShieldAlert className="w-3 h-3 mt-0.5 shrink-0" />
              <span>
                {kiki.flagReason}
                {kiki.flagCategories?.length
                  ? ` (${kiki.flagCategories.map((c) => CATEGORY_LABELS[c] ?? c).join(", ")})`
                  : ""}
              </span>
            </p>
          )}
          {kiki.note && (
            <p className="text-sm mt-2 text-neutral-300 italic line-clamp-2">"{kiki.note}"</p>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-lg font-bold leading-none">{kiki.applicationCount}</div>
          <div className="text-[10px] text-dark-muted uppercase tracking-wide">
            {kiki.applicationCount === 1 ? "applicant" : "applicants"}
          </div>
          {kiki.acceptedCount > 0 && (
            <div className="text-[11px] text-emerald-400 mt-1">{kiki.acceptedCount} accepted</div>
          )}
        </div>
      </div>
    </motion.button>
  );
}

// ---------------------------------------------------------------------------
// Detail modal
// ---------------------------------------------------------------------------

function ApplicationCard({ app }: { app: AdminKikiApplication }) {
  return (
    <div className="rounded-xl border border-dark-border p-3 space-y-2">
      <div className="flex items-start gap-2">
        <Avatar url={app.applicantProfileImageUrl} name={app.applicantName} size={30} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium truncate">{app.applicantName}</span>
            <AppStatusBadge status={app.status} />
          </div>
          {app.applicantEmail && (
            <a href={`mailto:${app.applicantEmail}`} className="text-xs text-dark-muted hover:text-primary-400">
              {app.applicantEmail}
            </a>
          )}
        </div>
        <span className="text-[11px] text-dark-muted shrink-0">
          {new Date(app.createdAt).toLocaleDateString()}
        </span>
      </div>
      {app.note && <p className="text-sm text-neutral-300 italic">"{app.note}"</p>}
      {(app.sharedProfile?.instagram || (app.sharedProfile?.interests?.length ?? 0) > 0) && (
        <div className="flex items-center gap-2 flex-wrap pt-1">
          {app.sharedProfile?.instagram && (
            <a
              href={`https://instagram.com/${app.sharedProfile.instagram.replace(/^@/, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-pink-400 hover:underline"
            >
              <Instagram className="w-3 h-3" /> @{app.sharedProfile.instagram.replace(/^@/, "")}
            </a>
          )}
          {app.sharedProfile?.interests?.map((t) => (
            <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-dark-card text-dark-muted">
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function KikiDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: kiki, isLoading } = useAdminKikiDetail(id);
  const setStatus = useSetKikiStatus();
  const reviewFlag = useReviewKikiFlag();
  const toast = useToast();
  const [confirmTakedown, setConfirmTakedown] = useState(false);

  const handleStatus = (status: KikiStatus, successMsg: string) => {
    setStatus.mutate(
      { id, status },
      {
        onSuccess: (res) => {
          if (res.success) {
            toast.success(successMsg);
            setConfirmTakedown(false);
          } else {
            toast.error(res.error || "Couldn't update kiki");
          }
        },
        onError: (err) => toast.error((err as Error).message || "Couldn't update kiki"),
      },
    );
  };

  const handleReviewFlag = (reviewed: boolean) => {
    reviewFlag.mutate(
      { id, reviewed },
      {
        onSuccess: (res) => {
          if (res.success) toast.success(reviewed ? "Flag dismissed" : "Flag re-opened");
          else toast.error(res.error || "Couldn't update flag");
        },
        onError: (err) => toast.error((err as Error).message || "Couldn't update flag"),
      },
    );
  };

  const prefs = kiki?.preferences;
  const hasPrefs =
    prefs &&
    (prefs.ageMin != null ||
      prefs.ageMax != null ||
      (prefs.genders?.length ?? 0) > 0 ||
      (prefs.orientations?.length ?? 0) > 0 ||
      (prefs.interests?.length ?? 0) > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative bg-white dark:bg-neutral-900 border border-dark-border rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-4 border-b border-dark-border bg-white dark:bg-neutral-900">
          <h2 className="text-lg font-bold truncate">
            {kiki ? kikiTitle(kiki) : "Kiki"}
          </h2>
          <button onClick={onClose} className="btn btn-ghost btn-sm p-2 shrink-0" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {isLoading || !kiki ? (
          <div className="flex items-center justify-center py-24">
            <Loader />
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <KindBadge kind={kiki.kind} />
              <KikiStatusBadge status={kiki.status} />
              {kiki.flaggedAt && <FlagBadge needsReview={kiki.needsReview} />}
              {kiki.cityName && (
                <span className="inline-flex items-center gap-1 text-xs text-dark-muted">
                  <MapPin className="w-3 h-3" /> {kiki.cityName}
                </span>
              )}
              <span className="text-xs text-dark-muted">
                Created {new Date(kiki.createdAt).toLocaleString()}
              </span>
            </div>

            {/* Moderation flag banner */}
            {kiki.flaggedAt && (
              <div
                className={`rounded-xl p-3 border ${
                  kiki.needsReview
                    ? "border-red-500/40 bg-red-500/10"
                    : "border-dark-border bg-dark-card"
                }`}
              >
                <div className="flex items-start gap-2">
                  <ShieldAlert
                    className={`w-4 h-4 mt-0.5 shrink-0 ${kiki.needsReview ? "text-red-400" : "text-dark-muted"}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">
                      {kiki.needsReview ? "Flagged by content scanner" : "Flag reviewed"}
                    </div>
                    {kiki.flagReason && <p className="text-sm text-neutral-300 mt-0.5">{kiki.flagReason}</p>}
                    {kiki.flagCategories && kiki.flagCategories.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {kiki.flagCategories.map((c) => (
                          <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-red-500/15 text-red-300">
                            {CATEGORY_LABELS[c] ?? c}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {kiki.needsReview ? (
                    <button
                      onClick={() => handleReviewFlag(true)}
                      disabled={reviewFlag.isPending}
                      className="btn btn-sm bg-dark-border hover:bg-neutral-700 border-0 shrink-0"
                      title="Mark as a false positive — removes it from the review queue"
                    >
                      {reviewFlag.isPending ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      Dismiss flag
                    </button>
                  ) : (
                    <button
                      onClick={() => handleReviewFlag(false)}
                      disabled={reviewFlag.isPending}
                      className="btn btn-sm btn-ghost text-dark-muted shrink-0"
                      title="Put it back in the review queue"
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Re-open
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Poster */}
            <div className="rounded-xl bg-dark-card p-4">
              <div className="text-xs uppercase tracking-wide text-dark-muted mb-2">Poster</div>
              <div className="flex items-center gap-3">
                <Avatar url={kiki.posterProfileImageUrl} name={kiki.posterName} size={40} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{kiki.posterName}</div>
                  {kiki.posterEmail && (
                    <div className="text-xs text-dark-muted truncate">{kiki.posterEmail}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {kiki.posterEmail && (
                    <a
                      href={`mailto:${kiki.posterEmail}?subject=${encodeURIComponent("Your Outclose kiki")}`}
                      className="btn btn-sm bg-dark-border hover:bg-neutral-700 border-0"
                      title="Email poster"
                    >
                      <Mail className="w-3.5 h-3.5 mr-1.5" /> Contact
                    </a>
                  )}
                  <a
                    href={`/admin/users?q=${encodeURIComponent(kiki.posterEmail ?? kiki.posterName)}`}
                    className="btn btn-ghost btn-sm p-2"
                    title="View in Users"
                  >
                    <Users className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>

            {/* Note */}
            {kiki.note && (
              <div>
                <div className="text-xs uppercase tracking-wide text-dark-muted mb-1">Note</div>
                <p className="text-sm text-neutral-200 italic">"{kiki.note}"</p>
              </div>
            )}

            {/* Event / activity context */}
            <div>
              <div className="text-xs uppercase tracking-wide text-dark-muted mb-1">
                {kiki.kind === "event" ? "Event" : "Activity"}
              </div>
              {kiki.kind === "event" ? (
                <div className="flex items-center gap-2 text-sm">
                  <span>{kiki.eventName ?? "Unknown event"}</span>
                  {kiki.eventDate && <span className="text-dark-muted">· {kiki.eventDate}</span>}
                  {kiki.eventSlug && (
                    <a
                      href={`/events/${kiki.eventSlug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-400 hover:underline inline-flex items-center gap-1"
                    >
                      view <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              ) : (
                <div className="text-sm">
                  {kiki.activityInfo?.emoji ? `${kiki.activityInfo.emoji} ` : ""}
                  {kiki.activityInfo?.name || kiki.activity || "—"}
                  {kiki.activityInfo?.date && <span className="text-dark-muted"> · {kiki.activityInfo.date}</span>}
                  {kiki.activityInfo?.location && (
                    <span className="text-dark-muted"> · {kiki.activityInfo.location}</span>
                  )}
                </div>
              )}
            </div>

            {/* Preferences */}
            {hasPrefs && prefs && (
              <div>
                <div className="text-xs uppercase tracking-wide text-dark-muted mb-1">Looking for</div>
                <div className="flex items-center gap-1.5 flex-wrap text-xs">
                  {(prefs.ageMin != null || prefs.ageMax != null) && (
                    <span className="px-2 py-0.5 rounded-full bg-dark-card text-dark-muted">
                      Age {prefs.ageMin ?? "?"}–{prefs.ageMax ?? "?"}
                    </span>
                  )}
                  {prefs.genders?.map((g) => (
                    <span key={g} className="px-2 py-0.5 rounded-full bg-dark-card text-dark-muted">{g}</span>
                  ))}
                  {prefs.orientations?.map((o) => (
                    <span key={o} className="px-2 py-0.5 rounded-full bg-dark-card text-dark-muted">{o}</span>
                  ))}
                  {prefs.interests?.map((i) => (
                    <span key={i} className="px-2 py-0.5 rounded-full bg-primary-500/15 text-primary-300">{i}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Applications */}
            <div>
              <div className="text-xs uppercase tracking-wide text-dark-muted mb-2 flex items-center gap-2">
                Applicants ({kiki.applications.length})
              </div>
              {kiki.applications.length === 0 ? (
                <p className="text-sm text-dark-muted">No one has reached out yet.</p>
              ) : (
                <div className="space-y-2">
                  {kiki.applications.map((a) => (
                    <ApplicationCard key={a.id} app={a} />
                  ))}
                </div>
              )}
              <p className="text-[11px] text-dark-muted mt-2">
                Private kiki chat lives in the app and isn't shown here.
              </p>
            </div>

            {/* Actions */}
            <div className="pt-3 border-t border-dark-border">
              {kiki.status === "cancelled" ? (
                <button
                  onClick={() => handleStatus("active", "Kiki restored")}
                  disabled={setStatus.isPending}
                  className="btn btn-sm bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border-0"
                >
                  {setStatus.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Restore kiki
                </button>
              ) : confirmTakedown ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-dark-muted">Hide this kiki from everyone?</span>
                  <button
                    onClick={() => handleStatus("cancelled", "Kiki taken down")}
                    disabled={setStatus.isPending}
                    className="btn btn-sm bg-red-600/20 hover:bg-red-600/30 text-red-400 border-0"
                  >
                    {setStatus.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Yes, take down
                  </button>
                  <button
                    onClick={() => setConfirmTakedown(false)}
                    className="btn btn-sm btn-ghost text-dark-muted"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmTakedown(true)}
                  className="btn btn-sm bg-red-600/20 hover:bg-red-600/30 text-red-400 border-0"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Take down
                </button>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const STATUS_TABS: { value: "needs_review" | "all" | KikiStatus; label: string }[] = [
  { value: "needs_review", label: "Needs review" },
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "fulfilled", label: "Fulfilled" },
  { value: "cancelled", label: "Taken down" },
];

const KIND_TABS: { value: "all" | KikiKind; label: string }[] = [
  { value: "all", label: "All types" },
  { value: "event", label: "Event" },
  { value: "standalone", label: "Standalone" },
];

const SORT_OPTIONS: { value: NonNullable<AdminKikisFilters["sortBy"]>; label: string }[] = [
  { value: "createdAt", label: "Created" },
  { value: "applications", label: "Applicants" },
];

type StatusTab = "needs_review" | "all" | KikiStatus;

export default function AdminKikiReview() {
  const [statusTab, setStatusTab] = useState<StatusTab>("all");
  const [kindTab, setKindTab] = useState<"all" | KikiKind>("all");
  const [cityId, setCityId] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<NonNullable<AdminKikisFilters["sortBy"]>>("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [openId, setOpenId] = useState<string | null>(null);
  const toast = useToast();
  const scan = useScanKikis();

  const needsReviewOnly = statusTab === "needs_review";
  const filters: AdminKikisFilters = {
    status: needsReviewOnly ? "all" : statusTab,
    kind: kindTab,
    cityId: cityId !== "all" ? cityId : undefined,
    search: search || undefined,
    sortBy,
    sortDir,
    needsReview: needsReviewOnly || undefined,
    limit: 200,
  };

  const { data, isLoading } = useAdminKikis(filters);

  const handleScan = () => {
    scan.mutate(undefined, {
      onSuccess: (res) => {
        if (res.success && res.data) {
          const { scanned, flagged, failed } = res.data;
          toast.success(
            scanned === 0 && failed === 0
              ? "All kikis already scanned"
              : `Scanned ${scanned}${flagged ? `, ${flagged} flagged` : ", none flagged"}${failed ? ` (${failed} failed)` : ""}`,
          );
        } else {
          toast.error(res.error || "Scan failed");
        }
      },
      onError: (err) => toast.error((err as Error).message || "Scan failed"),
    });
  };

  const kikis = data?.kikis ?? [];
  const summary = data?.summary;
  const byCity = useMemo(() => summary?.byCity ?? [], [summary]);

  const toggleSort = (col: NonNullable<AdminKikisFilters["sortBy"]>) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(col);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ col }: { col: NonNullable<AdminKikisFilters["sortBy"]> }) => {
    if (sortBy !== col) return <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />;
  };

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold mb-2">Kiki Review</h1>
            <p className="text-dark-muted">
              Monitor buddy posts ("kikis") across events and standalone activities — drill into applicants
              and take problematic ones down. New posts are auto-scanned for explicit content.
            </p>
          </div>
          <button
            type="button"
            onClick={handleScan}
            disabled={scan.isPending}
            title="Run the explicit-content scanner over kikis that haven't been scanned yet"
            className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-dark-card hover:bg-dark-border text-sm font-medium transition-colors disabled:opacity-50"
          >
            {scan.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ScanLine className="w-4 h-4 text-primary-400" />
            )}
            {scan.isPending ? "Scanning…" : "Scan now"}
          </button>
        </div>
      </motion.div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <StatCard label="Total kikis" value={summary.total} icon={Sparkles} />
          <StatCard label="Needs review" value={summary.needsReviewCount} icon={ShieldAlert} tone="bad" />
          <StatCard label="Active" value={summary.byStatus.active} icon={CheckCircle} tone="good" />
          <StatCard label="Fulfilled" value={summary.byStatus.fulfilled} icon={Sparkles} />
          <StatCard label="Taken down" value={summary.byStatus.cancelled} icon={Trash2} tone="bad" />
          <StatCard label="Standalone" value={summary.byKind.standalone} icon={PartyPopper} tone="warn" />
          <StatCard label="Applicants" value={summary.totalApplications} icon={Users} />
        </div>
      )}

      {/* Status + kind tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex gap-1 bg-dark-card rounded-xl p-1 w-fit">
          {STATUS_TABS.map((tab) => {
            const count =
              !summary ? undefined :
              tab.value === "all" ? summary.total :
              tab.value === "needs_review" ? summary.needsReviewCount :
              summary.byStatus[tab.value];
            const isActive = statusTab === tab.value;
            const isFlagTab = tab.value === "needs_review";
            const flagActive = isFlagTab && count != null && count > 0;
            return (
              <button
                key={tab.value}
                onClick={() => setStatusTab(tab.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  isActive
                    ? isFlagTab ? "bg-red-500 text-white" : "bg-primary-500 text-white"
                    : flagActive ? "text-red-300 hover:text-red-200" : "text-dark-muted hover:text-white"
                }`}
              >
                {isFlagTab && <ShieldAlert className="w-3.5 h-3.5" />}
                {tab.label}
                {count != null && count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    isActive ? "bg-white/20" : flagActive ? "bg-red-500/30 text-red-200" : "bg-dark-border"
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex gap-1 bg-dark-card rounded-xl p-1 w-fit">
          {KIND_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setKindTab(tab.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                kindTab === tab.value ? "bg-primary-500 text-white" : "text-dark-muted hover:text-white"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* City filter */}
      {byCity.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-1.5 mb-2">
            <MapPin className="w-3.5 h-3.5 text-dark-muted" />
            <span className="text-xs font-medium text-dark-muted uppercase tracking-wide">By City</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCityId("all")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm border transition-colors ${
                cityId === "all"
                  ? "bg-primary-500/20 border-primary-500/50 text-white"
                  : "border-dark-border text-dark-muted hover:text-white hover:border-neutral-500"
              }`}
            >
              <span className="font-medium">All Cities</span>
            </button>
            {byCity.map((c) => (
              <button
                key={c.cityId}
                onClick={() => setCityId(c.cityId)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm border transition-colors ${
                  cityId === c.cityId
                    ? "bg-primary-500/20 border-primary-500/50 text-white"
                    : "border-dark-border text-dark-muted hover:text-white hover:border-neutral-500"
                }`}
              >
                <span className="font-medium">{c.cityName}</span>
                <span className="text-xs text-dark-muted">{c.total}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search + sort */}
      <div className="flex gap-3 mb-6 flex-wrap items-center">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-muted pointer-events-none" />
          <input
            type="text"
            placeholder="Search poster, note, activity…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-9 w-full"
          />
        </div>
        <div className="flex items-center gap-1 text-sm text-dark-muted">
          <span>Sort:</span>
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => toggleSort(opt.value)}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg transition-colors ${
                sortBy === opt.value ? "bg-dark-card text-white" : "hover:bg-dark-card text-dark-muted"
              }`}
            >
              {opt.label}
              <SortIcon col={opt.value} />
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader />
        </div>
      ) : kikis.length === 0 ? (
        <div className="card text-center py-16">
          {needsReviewOnly ? (
            <>
              <ShieldCheck className="w-12 h-12 text-emerald-400/70 mx-auto mb-3" />
              <p className="text-lg font-semibold">Nothing needs review</p>
              <p className="text-dark-muted text-sm mt-1">No flagged kikis are waiting. All clear.</p>
            </>
          ) : (
            <>
              <Sparkles className="w-12 h-12 text-dark-muted mx-auto mb-3" />
              <p className="text-lg font-semibold">No kikis found</p>
              <p className="text-dark-muted text-sm mt-1">Try adjusting your filters.</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {kikis.map((kiki) => (
            <KikiRow key={kiki.id} kiki={kiki} onOpen={setOpenId} />
          ))}
        </div>
      )}

      <AnimatePresence>
        {openId && <KikiDetailModal id={openId} onClose={() => setOpenId(null)} />}
      </AnimatePresence>
    </div>
  );
}
